// components/AdminCoinToVipPlans.js
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getSocket } from "../../utils/socket";
import { FaEdit, FaTrash } from "react-icons/fa";
import { motion } from "framer-motion";

const AdminCoinToVipPlans = () => {
  const { user, fetchWithRetry, isLoading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [newPlan, setNewPlan] = useState({
    membershipPlanId: "",
    coinPrice: "",
    coinDiscount: "",
    discountStartDate: "",
    discountEndDate: "",
  });
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // State cho tìm kiếm, lọc, sắp xếp, phân trang
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithRetry({ method: "get", url: "/api/admin/coin-to-vip-plans" });
      setPlans(res.data);
      setFilteredPlans(res.data);
      setLoading(false);
    } catch (error) {
      setError("Không thể tải danh sách gói VIP đổi bằng coin!");
      setLoading(false);
    }
  }, [fetchWithRetry]);

  const handleAddPlan = async () => {
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/admin/coin-to-vip-plans",
        data: newPlan,
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      setNewPlan({ membershipPlanId: "", coinPrice: "", coinDiscount: "", discountStartDate: "", discountEndDate: "" });
      setShowAddModal(false);
      await loadPlans();
    } catch (error) {
      setError(error.response?.data?.message || "Không thể thêm thông tin coin cho gói!");
    }
  };

  const handleUpdatePlan = async () => {
    try {
      const response = await fetchWithRetry({
        method: "put",
        url: `/api/admin/coin-to-vip-plans/${editingPlan.coinPlanId}`,
        data: {
          coinPrice: editingPlan.coinPrice,
          coinDiscount: editingPlan.coinDiscount,
          discountStartDate: editingPlan.discountStartDate,
          discountEndDate: editingPlan.discountEndDate,
        },
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      setEditingPlan(null);
      setShowEditModal(false);
      await loadPlans();
    } catch (error) {
      setError(error.response?.data?.message || "Không thể cập nhật thông tin coin cho gói!");
    }
  };

  const handleDeletePlan = async (coinPlanId) => {
    if (!window.confirm("Bạn có chắc muốn xóa thông tin coin của gói này?")) return;
    try {
      const response = await fetchWithRetry({
        method: "delete",
        url: `/api/admin/coin-to-vip-plans/${coinPlanId}`,
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadPlans();
    } catch (error) {
      setError(error.response?.data?.message || "Không thể xóa thông tin coin của gói!");
    }
  };

  // Lọc và sắp xếp danh sách gói
  useEffect(() => {
    let filtered = [...plans];

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (plan) =>
          plan.name.toLowerCase().includes(lowerSearchTerm) ||
          (plan.role && plan.role.toLowerCase().includes(lowerSearchTerm))
      );
    }

    filtered.sort((a, b) => {
      const fieldA = a[sortField] || "";
      const fieldB = b[sortField] || "";
      if (sortField === "createdAt" || sortField === "updatedAt") {
        return sortOrder === "asc" ? new Date(fieldA) - new Date(fieldB) : new Date(fieldB) - new Date(fieldA);
      } else if (sortField === "coinPrice" || sortField === "duration" || sortField === "coinDiscount") {
        return sortOrder === "asc" ? (fieldA || 0) - (fieldB || 0) : (fieldB || 0) - (fieldA || 0);
      } else {
        return sortOrder === "asc"
          ? fieldA.toString().localeCompare(fieldB.toString())
          : fieldB.toString().localeCompare(fieldA.toString());
      }
    });

    setFilteredPlans(filtered);
  }, [plans, searchTerm, sortField, sortOrder]);

  // Phân trang
  const totalItems = filteredPlans.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPlans = filteredPlans.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "mod")) {
      navigate("/login");
      return;
    }
    loadPlans();

    const socket = getSocket();
    socket.on("notification", (notification) => {
      if (notification.type === "coin_to_vip_plan_updated") {
        setSuccessMessage(`Gói VIP đổi bằng coin đã được cập nhật: ${notification.message}`);
        loadPlans();
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    });

    return () => {
      socket.off("notification");
    };
  }, [user, navigate, loadPlans, isLoading]);

  if (loading) return <p className="text-center text-gray-500">Đang tải...</p>;

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Quản lý gói VIP đổi bằng Coin</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Thiết lập số coin cho gói
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          {error.includes("Phiên làm việc hết hạn") ? (
            <button
              onClick={() => navigate("/login")}
              className="ml-4 px-4 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
            >
              Đăng nhập lại
            </button>
          ) : (
            <button
              onClick={() => setError(null)}
              className="ml-4 px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
            >
              Đóng
            </button>
          )}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{successMessage}</div>
      )}

      {filteredPlans.length === 0 && !error && !loading && (
        <p className="text-center text-gray-500">Không có gói VIP nào được tìm thấy.</p>
      )}

      {filteredPlans.length > 0 && (
        <>
          <div className="mb-6 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Tìm kiếm theo tên gói..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="overflow-x-auto"
          >
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead>
                <tr className="bg-blue-500 text-white">
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("name")}>
                    Tên gói {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("duration")}>
                    Thời hạn (tháng) {sortField === "duration" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("coinPrice")}>
                    Số coin cần thiết {sortField === "coinPrice" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("coinDiscount")}>
                    Giảm giá (%) {sortField === "coinDiscount" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left">Hành động</th>
                </tr>
              </thead>
              <tbody>
                {currentPlans.map((plan) => (
                  <tr key={plan._id} className="border-b hover:bg-gray-50 transition duration-200">
                    <td className="p-4">{plan.name}</td>
                    <td className="p-4">{plan.duration}</td>
                    <td className="p-4">{plan.coinPrice ? plan.coinPrice.toLocaleString() : "Chưa thiết lập"}</td>
                    <td className="p-4">{plan.coinDiscount}%</td>
                    <td className="p-4 space-x-2">
                      <button
                        onClick={() => {
                          setEditingPlan(plan);
                          setShowEditModal(true);
                        }}
                        className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                        disabled={!plan.coinPrice}
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeletePlan(plan.coinPlanId)}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                        disabled={!plan.coinPrice}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          <div className="mt-6 flex justify-between items-center">
            <div>
              <label className="mr-2">Hiển thị:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="p-2 border border-gray-300 rounded-lg"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="ml-2">mục/trang</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-1 bg-blue-500 text-white rounded-lg disabled:bg-gray-300"
              >
                Trước
              </button>
              <span className="px-4 py-1">
                Trang {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-1 bg-blue-500 text-white rounded-lg disabled:bg-gray-300"
              >
                Sau
              </button>
            </div>
          </div>
        </>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Thiết lập số coin cho gói</h3>
            <select
              value={newPlan.membershipPlanId}
              onChange={(e) => setNewPlan({ ...newPlan, membershipPlanId: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            >
              <option value="">Chọn gói VIP</option>
              {plans
                .filter((plan) => !plan.coinPrice) // Chỉ hiển thị các gói chưa được thiết lập số coin
                .map((plan) => (
                  <option key={plan._id} value={plan._id}>
                    {plan.name}
                  </option>
                ))}
            </select>
            <input
              type="number"
              placeholder="Số coin cần thiết"
              value={newPlan.coinPrice}
              onChange={(e) => setNewPlan({ ...newPlan, coinPrice: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="number"
              placeholder="Giảm giá (%)"
              value={newPlan.coinDiscount}
              onChange={(e) => setNewPlan({ ...newPlan, coinDiscount: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="date"
              placeholder="Ngày bắt đầu khuyến mãi"
              value={newPlan.discountStartDate}
              onChange={(e) => setNewPlan({ ...newPlan, discountStartDate: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="date"
              placeholder="Ngày kết thúc khuyến mãi"
              value={newPlan.discountEndDate}
              onChange={(e) => setNewPlan({ ...newPlan, discountEndDate: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Hủy
              </button>
              <button
                onClick={handleAddPlan}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Chỉnh sửa thông tin coin cho gói</h3>
            <p className="text-gray-700 mb-3">Gói: {editingPlan.name}</p>
            <input
              type="number"
              placeholder="Số coin cần thiết"
              value={editingPlan.coinPrice}
              onChange={(e) => setEditingPlan({ ...editingPlan, coinPrice: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="number"
              placeholder="Giảm giá (%)"
              value={editingPlan.coinDiscount}
              onChange={(e) => setEditingPlan({ ...editingPlan, coinDiscount: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="date"
              placeholder="Ngày bắt đầu khuyến mãi"
              value={editingPlan.discountStartDate ? new Date(editingPlan.discountStartDate).toISOString().split("T")[0] : ""}
              onChange={(e) => setEditingPlan({ ...editingPlan, discountStartDate: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="date"
              placeholder="Ngày kết thúc khuyến mãi"
              value={editingPlan.discountEndDate ? new Date(editingPlan.discountEndDate).toISOString().split("T")[0] : ""}
              onChange={(e) => setEditingPlan({ ...editingPlan, discountEndDate: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Hủy
              </button>
              <button
                onClick={handleUpdatePlan}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Cập nhật
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoinToVipPlans;